import type {AiImagePanelProps, AiImageModels} from '@Panels/ai-image-panel/ai-image-panel.types';
import {AI_IMAGE_PROMPT_MAX_LENGTH} from '@Panels/ai-image-panel/ai-image-panel.types';
import {AiImagePanelState} from '@Panels/ai-image-panel/ai-image-panel.types';
import type {MouseEventHandler, ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {Panel, PanelContent, PanelFooter, PanelFooterPrimaryDefaultAction, PanelHeader} from '@Components/panel';
import {InputFieldState, InputFieldType} from '@Components/input-field-v2/input-field.types';
import type {AITextToImageBackendData, GenerateAIImageResponse, StabilityAIStylePreset} from '@Libraries/pmw-stock-media-library';
import {
  AspectRatio,
  GENERATE_IMAGE_FLAGGED_PROMPT_RESPONSE_CODE,
  GENERATE_IMAGE_INSUFFICIENT_CREDITS_RESPONSE_CODE,
  GENERATE_IMAGE_UNSUPPORTED_LANGUAGE_RESPONSE_CODE,
  generateImage as generateAiImage,
  getRemainingTextToImageUsages,
  setRecentStockItem,
} from '@Libraries/pmw-stock-media-library';
import {addItemsToGrid, deleteGridItems, getSelectedGridItems, replaceGridItem, toggleGridItemSelection, updateGridItem} from '@Components/base-grid';
import {Size} from '@Components/button';
import {InputField} from '@Components/input-field-v2';
import {PillButton} from '@Components/pill-button';
import {PillButtonSize} from '@Components/pill-button/pill-button.types';
import {
  getAiImageFilterForAspectRatio,
  getAiImageFilterForStylePreset,
  getCostInCreditsForModel,
  getDropdownItemsForAspectRatioFilterForAIImage,
  getDropdownItemsForStyleFilterForAIImage,
  getGridID,
  getGridItemForAIGeneratedImageV2,
  getPlaceholderTextForAiImagePrompt,
  getTextForModelFilter,
  uploadSelectedItem,
} from '@Panels/stock-panel/stock-panel.library';
import {AiImageFilterPopUp} from '@Panels/stock-panel/components/ai-image-filter-pop-up';
import {TextToImageGrid} from '@Components/text-to-image-grid';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {AI_IMAGE_LOADING_SKELETONS, MIN_STOCK_ITEMS_TO_DISPLAY, StockPanelTabs} from '@Panels/stock-panel/stock-panel.types';
import type {GridSkeletonItemStorage} from '@Components/base-grid/components/grid-skeleton-item';
import type {GridAiImageItemStorage} from '@Components/base-grid/components/grid-stock-image-item';
import {getUserId, isUserPremium} from '@Libraries/user.library';
import {getGridIdForPanel, showTextToImageUpsellDialog} from '@Libraries/text-to-image-library';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {MODAL_IDS, openModal} from '@Components/modal-container-deprecated';
import {setRecentStockAvailability} from '@Panels/stock-panel/stock-panel-reducer';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {isMobile} from 'react-device-detect';
import type {TextToImageGridProps} from '@Components/text-to-image-grid/text-to-image-grid.types';
import {TextToImageGridErrorState} from '@Components/text-to-image-grid/text-to-image-grid.types';
import {AiImageState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {AiButton} from '@Components/ai-button';
import {AIButtonVariant} from '@Components/ai-button/ai-button.types';
import {Text, TextSize, TextType} from '@Components/text';
import {formatNumber, formatToThousand} from '@Utils/number.util';
import type {BuyCreditsResponse} from '@Components/remove-bg-purchase-modal';
import styles from './ai-image-panel.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {updateAiImageState, updateIsUploadingToMyUploads} from '@/components/poster-editor/poster-editor-reducer';
import {IconSize} from '@Components/icon-v2/icon.types';
import {useLeftRightScrollShadow} from '@Hooks/useLeftRightScrollShadow';
import {updateSelectedAiImageModelId, updateSelectedAiImageModelIdBackend} from '@/reducers/user-preferences.reducer';
import {noop} from '@Utils/general.util';
import {AiImageModelOptions} from '@Panels/ai-image-panel/components/ai-image-model-options/ai-image-model-options';

const SELECTION_ID = 'TEXT_TO_IMAGE_POP_OVER_SELECTION';
const PANEL_ID = 'TEXT_TO_IMAGE_PANEL_FALLBACK_ID';

export default function AiImagePanel({populateRecentlyAddedOnItemAdd = true, ...props}: AiImagePanelProps): ReactElement<any> {
  const [prompt, setPrompt] = useState('');
  const [remainingUsages, setRemainingUsages] = useState(0);
  const [selectedAIImageAspectRatio, setSelectedAIImageAspectRatio] = useState<AspectRatio | undefined>(undefined);
  const [selectedAIImageStyle, setSelectedAIImageStyle] = useState<StabilityAIStylePreset | undefined>(undefined);
  const [panelState, setPanelState] = useState(AiImagePanelState.GENERATION);
  const [errorState, setErrorState] = useState<TextToImageGridErrorState>(TextToImageGridErrorState.NONE);
  const lastPrompt = useRef<string | undefined>(undefined);
  const selectionIDRef = useRef(props.selectionID ?? SELECTION_ID);
  const inputRef = useRef<HTMLInputElement>(null);
  const isPremiumRef = useRef(typeof getUserId() !== 'undefined' && isUserPremium());
  const gridItemIdsProcessingRef = useRef<Record<string, true>>({});
  const promptFilterContainerRef = useRef<HTMLUListElement>(null);
  const {leftShadow, rightShadow} = useLeftRightScrollShadow(promptFilterContainerRef, !!promptFilterContainerRef.current);
  const selectedItems = useAppSelector((state) => {
    return getSelectedGridItems(state, selectionIDRef.current);
  });
  const aiImageState = useAppSelector((state) => {
    return state.posterEditor.aiState.aiImage;
  });
  const selectedAiModelId = useAppSelector((state) => {
    return state.userPreferences.selectedAiImageModelId;
  });
  const dispatch = useAppDispatch();
  const panelId = useRef(props.panelId ?? PANEL_ID);
  const aiImagePromptPlaceholderText = useRef<string>(getPlaceholderTextForAiImagePrompt());
  const gridIDRef = useRef(getGridIdForPanel(panelId.current));
  const onClose: () => void = props.onClose
    ? (): void => {
        if (props.onClose) {
          props.onClose();
        }
      }
    : useDefaultPanelClose(panelId.current);

  const isTextToImageGridInErrorState = (): TextToImageGridErrorState => {
    return aiImageState === AiImageState.GENERATING ? TextToImageGridErrorState.NONE : errorState;
  };

  const onGridItemClicked = (item: GridItemStorage, isDoubleClick: boolean): void => {
    if (isMobile) {
      if (isDoubleClick && selectedItems.length === 1) {
        void onImageClicked([item]);
      } else {
        dispatch(
          toggleGridItemSelection({
            gridItemId: item.id,
            gridSelectionGroupId: selectionIDRef.current,
          })
        );
      }
    } else {
      void onImageClicked([item]);
    }
  };

  const getAIImageGrid = (): ReactElement<any> => {
    const gridProps: TextToImageGridProps = {
      customOnGridItemClicked: onGridItemClicked,
      gridClasses: `spacing-p-t-3 spacing-p-r-4 spacing-p-b-3 spacing-p-l-4 ${styles.grid}`,
      gridSelectionGroupId: selectionIDRef.current,
      maxSelectionNumber: 1,
      gridId: gridIDRef.current,
      isHidden: panelState !== AiImagePanelState.GENERATION,
      isLoading: aiImageState === AiImageState.GENERATING,
      errorState: isTextToImageGridInErrorState(),
      infographicClasses: styles.infographicImage,
      infographicTextClasses: styles.infographicText,
    };

    return <TextToImageGrid {...gridProps} key={gridIDRef.current} />;
  };

  const getPanelState = (): ReactElement<any> => {
    return (
      <>
        {getPrompt()}
        {getAIImageGrid()}
        <AiImageFilterPopUp
          onClick={(id) => {
            setSelectedAIImageStyle(id as StabilityAIStylePreset);
            setPanelState(AiImagePanelState.GENERATION);
          }}
          className={`${styles.stylePopUpListStyle} spacing-p-4 ${panelState === AiImagePanelState.STYLE_PRESET ? '' : '_hidden'}`}
          dropdownItems={getDropdownItemsForStyleFilterForAIImage()}
          selectedItemID={selectedAIImageStyle}
        />
        <AiImageFilterPopUp
          onClick={(id) => {
            setSelectedAIImageAspectRatio(id as AspectRatio);
            setPanelState(AiImagePanelState.GENERATION);
          }}
          className={`${styles.stylePopUpListStyle} spacing-p-4 ${panelState === AiImagePanelState.ASPECT_RATIO_PRESET ? '' : '_hidden'}`}
          dropdownItems={getDropdownItemsForAspectRatioFilterForAIImage()}
          selectedItemID={selectedAIImageAspectRatio}
          imageClasses={styles.aspectRatioThumbnail}
        />
        {getContentForModelSelector()}
      </>
    );
  };

  const getContentForModelSelector = (): ReactElement<any> => {
    return (
      <AiImageModelOptions
        selectedAiModelId={selectedAiModelId}
        onAiImageModelSelected={onAiImageModelSelected}
        className={panelState === AiImagePanelState.MODEL_PRESET ? '' : '_hidden'}
      />
    );
  };

  const onAiImageModelSelected = (modelId?: AiImageModels): void => {
    if (modelId) {
      void updateSelectedAiImageModelIdBackend(modelId);
    }
    dispatch(updateSelectedAiImageModelId(modelId));
    setPanelState(AiImagePanelState.GENERATION);
  };

  const getPanelTitle = (): string => {
    switch (panelState) {
      case AiImagePanelState.ASPECT_RATIO_PRESET:
        return window.i18next.t('pmwjs_aspect_ratio');
      case AiImagePanelState.STYLE_PRESET:
        return window.i18next.t('pmwjs_style');
      case AiImagePanelState.MODEL_PRESET:
        return window.i18next.t('pmwjs_model');
      default:
        return window.i18next.t('pmwjs_ai_images');
    }
  };

  const onBackButton: MouseEventHandler = (e) => {
    if (panelState !== AiImagePanelState.GENERATION) {
      setPanelState(AiImagePanelState.GENERATION);
    } else if (props.onBack) {
      props.onBack(e);
    }
  };

  const shouldShowHeader = (): boolean => {
    if (isMobile) {
      return true;
    }
    return panelState !== AiImagePanelState.GENERATION || !!props.onBack;
  };

  const getCTAText = (): string => {
    switch (errorState) {
      case TextToImageGridErrorState.NONE:
        return lastPrompt && lastPrompt.current === prompt ? window.i18next.t('pmwjs_generate_more') : window.i18next.t('pmwjs_generate');
      case TextToImageGridErrorState.GENERIC:
      case TextToImageGridErrorState.UNSUPPORTED_LANUAGE:
        return window.i18next.t('pmwjs_retry');
      case TextToImageGridErrorState.FLAGGED:
        return window.i18next.t('pmwjs_try_a_different_term');
      default:
        throw new Error(`Unhandled error state: ${errorState}`);
    }
  };

  const onImageClicked = async (items: GridItemStorage[]): Promise<void> => {
    onClose();
    showLoading('addStockMediaItem', {
      text: window.i18next.t('pmwjs_adding_1_of_1'),
    });

    let recentStockItemCount = 0;

    for (const item of items) {
      if (item.id in gridItemIdsProcessingRef.current) {
        continue;
      }

      gridItemIdsProcessingRef.current[item.id] = true;
      // loader on
      dispatch(
        updateGridItem({
          gridId: gridIDRef.current,
          itemId: item.id,
          changes: {
            isLoading: true,
          },
        })
      );
      try {
        dispatch(updateIsUploadingToMyUploads(true));

        const onAddItemResponse = await uploadSelectedItem(item, props.shouldLoadDataUrlImage);
        dispatch(updateIsUploadingToMyUploads(false));
        props.onAddSelected([onAddItemResponse]);

        if (populateRecentlyAddedOnItemAdd && isPremiumRef.current) {
          recentStockItemCount = await setRecentStockItem(item);
        }
      } catch (e) {
        console.error(e);

        openModal(MODAL_IDS.ERROR_MODAL_ID, {
          message: window.i18next.t('pmwjs_general_ajax_error'),
          log: false,
        });
      }

      dispatch(
        updateGridItem({
          gridId: gridIDRef.current,
          itemId: item.id,
          changes: {
            isLoading: false,
          },
        })
      );

      delete gridItemIdsProcessingRef.current[item.id];
    }

    dispatch(setRecentStockAvailability(recentStockItemCount >= MIN_STOCK_ITEMS_TO_DISPLAY));
    hideLoading('addStockMediaItem');
  };

  const getTooltipContent = (): ReactElement<any> => {
    return (
      <div className="flex-center flex-v-row">
        <Text type={TextType.BODY} size={TextSize.XSMALL} val={window.i18next.t('pmwjs_curently_credit_credits', {credit: formatNumber(remainingUsages)})} />
        <Text
          dangerouslySetInnerHTML
          type={TextType.BODY}
          size={TextSize.XSMALL}
          val={
            selectedAiModelId
              ? window.i18next.t('pmwjs_will_cost_x_credit', {creditsCost: getCostInCreditsForModel(selectedAiModelId)})
              : window.i18next.t('pmwjs_will_cost_1_credit')
          }
        />
      </div>
    );
  };

  const getPrimaryAction = (): ReactElement<any>[] => {
    if (selectedItems.length > 0) {
      return [
        <PanelFooterPrimaryDefaultAction
          key="stock-btn-cta-footer"
          text={window.i18next.t('pmwjs_add_selected')}
          onClick={() => {
            void onImageClicked(isMobile ? selectedItems : [selectedItems[0]]);
          }}
        />,
      ];
    }

    return [
      <AiButton
        isFullWidth
        pillToolTipContent={getTooltipContent()}
        icon={errorState !== TextToImageGridErrorState.NONE ? 'icon-refresh' : 'icon-ai-image'}
        isLoadingButtonOverlayShown
        key="ai-image-cta-footer"
        customClasses="_full-width _full-height"
        topPillContent={formatToThousand(remainingUsages)}
        size={Size.MEDIUM}
        disabled={isCTADisabled()}
        text={getCTAText()}
        onClick={onCtaClicked}
        isLoading={aiImageState === AiImageState.GENERATING}
        variant={!isPremiumRef.current ? AIButtonVariant.PREMIUM : undefined}
        pillClassName={!isPremiumRef.current ? `${styles.premiumPill} spacing-p-l-0 spacing-p-r-0` : 'spacing-p-r-1 spacing-p-l-1 '}
      />,
    ];
  };

  const onCtaClicked = (): void => {
    switch (errorState) {
      case TextToImageGridErrorState.NONE:
      case TextToImageGridErrorState.GENERIC:
        generateImage();
        break;
      case TextToImageGridErrorState.FLAGGED:
      case TextToImageGridErrorState.UNSUPPORTED_LANUAGE:
        setPrompt('');
        setErrorState(TextToImageGridErrorState.NONE);
        break;
      default:
        throw new Error(`Unhandled error state: ${errorState}`);
    }
  };

  const isCTADisabled = (): boolean => {
    return aiImageState === AiImageState.GENERATING ? false : prompt.trim().length === 0;
  };

  const getDimensionsForAiImageSkeletonItem = (): Partial<GridSkeletonItemStorage> => {
    switch (selectedAIImageAspectRatio) {
      case AspectRatio.PORTRAIT:
        return {width: 40};
      case AspectRatio.LANDSCAPE:
        return {width: 122};
      case AspectRatio.SQUARE:
      default:
        return {width: 70};
    }
  };

  const onGeneratedImagesReceived = (response: AITextToImageBackendData[]): void => {
    const items: GridAiImageItemStorage[] = response.map(getGridItemForAIGeneratedImageV2);
    const toReplace = [...AI_IMAGE_LOADING_SKELETONS];

    items.forEach((gridItem): void => {
      const skeleton = toReplace.pop();
      if (skeleton) {
        dispatch(replaceGridItem({gridId: gridIDRef.current, itemId: skeleton.id, gridItem}));
      }
    });

    if (toReplace.length > 0) {
      dispatch(
        deleteGridItems({
          gridId: gridIDRef.current,
          gridSelectionGroupId: selectionIDRef.current,
          itemIdsToDelete: toReplace.map((skeleton): string => {
            return skeleton.id;
          }),
        })
      );
    }

    setErrorState(TextToImageGridErrorState.NONE);
  };

  const addLoadingSkeletonsToGrid = (): void => {
    dispatch(
      addItemsToGrid({
        id: gridIDRef.current,
        index: 0,
        items: AI_IMAGE_LOADING_SKELETONS.map((skeletonItem): GridItemStorage => {
          return {...skeletonItem, ...getDimensionsForAiImageSkeletonItem()};
        }),
      })
    );
  };

  const removeLoadingSkeletonsFromGrid = (): void => {
    dispatch(
      deleteGridItems({
        gridId: getGridID(StockPanelTabs.TEXT_TO_IMAGE),
        gridSelectionGroupId: selectionIDRef.current,
        itemIdsToDelete: AI_IMAGE_LOADING_SKELETONS.map((item) => {
          return item.id;
        }),
      })
    );
  };

  const onGenerateImageSuccess = (generateImagesResponse: GenerateAIImageResponse): void => {
    onGeneratedImagesReceived(generateImagesResponse.generatedImages);
    setRemainingUsages(generateImagesResponse.remainingCredits);
    setErrorState(TextToImageGridErrorState.NONE);
  };

  const handleInsufficientCreditsError = (): void => {
    dispatch(
      deleteGridItems({
        gridId: gridIDRef.current,
        gridSelectionGroupId: selectionIDRef.current,
        itemIdsToDelete: AI_IMAGE_LOADING_SKELETONS.map((item) => {
          return item.id;
        }),
      })
    );

    window.PMW.util.require(typeof window.PMW.stripeCheckout === 'undefined', 'stripeCheckoutDialog', true, () => {
      window.PMW.stripeCheckout.showStripeCheckoutForCreditPackPurchase(
        (data: BuyCreditsResponse) => {
          if (data.credits > 0) {
            generateImage();
          }
        },
        noop,
        window.i18next.t('pmwjs_purchase_more_credits_continue_using_ai_images_bg_remover_and_more'),
        window.i18next.t('pmwjs_youre_out_of_credits')
      );
    });
  };

  const onGenerateImageError = (e: Error): void => {
    setErrorState((): TextToImageGridErrorState => {
      if (e.message === GENERATE_IMAGE_INSUFFICIENT_CREDITS_RESPONSE_CODE) {
        handleInsufficientCreditsError();
        return TextToImageGridErrorState.NONE;
      }
      if (e.message === GENERATE_IMAGE_FLAGGED_PROMPT_RESPONSE_CODE) {
        return TextToImageGridErrorState.FLAGGED;
      }
      if (e.message === GENERATE_IMAGE_UNSUPPORTED_LANGUAGE_RESPONSE_CODE) {
        return TextToImageGridErrorState.UNSUPPORTED_LANUAGE;
      }
      return TextToImageGridErrorState.GENERIC;
    });

    removeLoadingSkeletonsFromGrid();
  };

  const generateImage = (): void => {
    if (!isPremiumRef.current) {
      void showTextToImageUpsellDialog();
      return;
    }

    if (prompt.length > 0) {
      dispatch(updateAiImageState(AiImageState.GENERATING));
      addLoadingSkeletonsToGrid();

      trackPosterBuilderGA4Events(GA4EventName.AI_IMAGE_MENU_GENERATE, {
        [GA4EventParamName.TYPE]: selectedAIImageStyle ?? '',
      });

      lastPrompt.current = prompt;
      generateAiImage({prompt, style: selectedAIImageStyle, aspectRatio: selectedAIImageAspectRatio, providerId: selectedAiModelId})
        .then(onGenerateImageSuccess)
        .catch(onGenerateImageError)
        .finally(() => {
          dispatch(updateAiImageState(AiImageState.READY));
          setTimeout(() => {
            dispatch(updateAiImageState(AiImageState.IDLE));
          }, 2000);
        });
    }
  };

  const getAdditionalPromptRow = (): ReactElement<any> => {
    return (
      <div className={`_position-relative ${rightShadow ? styles.rightShadow : ''} ${leftShadow ? styles.leftShadow : ''}`}>
        <ul ref={promptFilterContainerRef} className={`flex-h-row flex-align-items-center spacing-m-0 _overflow-auto ${styles.additionalPromptsContainer}`}>
          <li key="style-additional-prompt" className="spacing-p-0">
            <PillButton
              isSelected={!!selectedAIImageStyle}
              isDisabled={aiImageState === AiImageState.GENERATING}
              size={PillButtonSize.SMALL}
              text={selectedAIImageStyle ? getAiImageFilterForStylePreset(selectedAIImageStyle).text : window.i18next.t('pmwjs_style')}
              onClick={(_, event) => {
                event?.preventDefault();
                event?.stopPropagation();
                setPanelState(AiImagePanelState.STYLE_PRESET);
              }}
              onClose={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedAIImageStyle(undefined);
              }}
            />
          </li>
          <li key="aspect-ratio-additional-prompt" className="spacing-p-0">
            <PillButton
              isSelected={!!selectedAIImageAspectRatio}
              isDisabled={aiImageState === AiImageState.GENERATING}
              size={PillButtonSize.SMALL}
              text={selectedAIImageAspectRatio ? getAiImageFilterForAspectRatio(selectedAIImageAspectRatio).text : window.i18next.t('pmwjs_aspect_ratio')}
              onClick={(_, event) => {
                event?.preventDefault();
                event?.stopPropagation();
                setPanelState(AiImagePanelState.ASPECT_RATIO_PRESET);
              }}
              onClose={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedAIImageAspectRatio(undefined);
              }}
            />
          </li>
          <li key="model-additional-prompt" className="spacing-p-0">
            <PillButton
              isSelected={!!selectedAiModelId}
              isDisabled={aiImageState === AiImageState.GENERATING}
              size={PillButtonSize.SMALL}
              text={selectedAiModelId ? getTextForModelFilter(selectedAiModelId) : window.i18next.t('pmwjs_model')}
              onClick={(_, event) => {
                event?.preventDefault();
                event?.stopPropagation();
                onAiModelSelectionDropdown();
              }}
              onClose={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onAiImageModelSelected(undefined);
              }}
            />
          </li>
        </ul>
      </div>
    );
  };

  const onAiModelSelectionDropdown = (): void => {
    const toTrack: Record<string, string> = {};
    if (selectedAIImageAspectRatio) {
      toTrack.aspect_ratio = selectedAIImageAspectRatio;
    }
    if (selectedAIImageStyle) {
      toTrack.style = selectedAIImageStyle;
    }
    if (selectedAiModelId) {
      toTrack.model = getTextForModelFilter(selectedAiModelId);
    }
    trackPosterBuilderGA4Events(GA4EventName.AI_IMAGE_MENU_MODEL, toTrack);
    setPanelState(AiImagePanelState.MODEL_PRESET);
  };

  const getPrompt = (): ReactElement<any> => {
    return (
      <div
        className={`flex-v-row spacing-p-t-4 spacing-p-r-4 spacing-p-b-3 spacing-p-l-4 ${styles.inputContainer} ${panelState === AiImagePanelState.GENERATION ? '' : '_hidden'}`}
      >
        <InputField
          maxLength={AI_IMAGE_PROMPT_MAX_LENGTH}
          isControlled
          isMultiline
          inputType={InputFieldType.SEARCH}
          input={prompt}
          onInputChange={(updatedTerm: string): void => {
            setPrompt(updatedTerm);
          }}
          onInputSubmit={() => {
            if (prompt.trim().length) {
              generateImage();
            }
          }}
          isDisabled={aiImageState === AiImageState.GENERATING}
          placeholder={aiImagePromptPlaceholderText.current}
          inputClassName={styles.inputField}
          ref={inputRef}
          state={errorState === TextToImageGridErrorState.FLAGGED ? InputFieldState.ERROR : InputFieldState.DEFAULT}
        />
        {getAdditionalPromptRow()}
      </div>
    );
  };

  const getRemainingTextToImageInvocations = async (): Promise<void> => {
    try {
      const usages = await getRemainingTextToImageUsages();
      setRemainingUsages(usages);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    void getRemainingTextToImageInvocations();
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <Panel
      className={`_full-width _full-height ${props.className ?? ''}`}
      panelHeader={
        <PanelHeader
          backButtonIconSize={!isMobile ? IconSize.SIZE_ICON_16 : undefined}
          titleClasses={panelState === AiImagePanelState.GENERATION ? styles.aiImageTitleText : undefined}
          className={`_borderless ${!isMobile ? 'spacing-m-l-3 spacing-p-t-4' : 'text-center'} ${shouldShowHeader() ? '' : '_hidden'}`}
          title={getPanelTitle()}
          showCloseButton={isMobile}
          onClose={isMobile ? onClose : undefined}
          showBackButton
          onBackButton={onBackButton}
        />
      }
      panelContent={<PanelContent className={`_full-width flex-v-row ${styles.panelContent}`}>{getPanelState()}</PanelContent>}
      panelFooter={
        <PanelFooter
          className={`${selectedItems.length > 0 ? '' : '_borderless'} ${panelState === AiImagePanelState.GENERATION ? '' : '_hidden'}`}
          key={selectionIDRef.current}
          primaryActions={getPrimaryAction()}
          border
          fullWidthPrimaryActions
        />
      }
    />
  );
}
